import { useParams, type LoaderFunctionArgs } from "react-router";

import { CtaBand } from "@/components/cms/CtaBand";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import {
  ProjectListSection,
  StoryListSection,
  chosenFocalPoint,
  visibleItems,
} from "@/components/cms/SectionRenderer";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { fetchPageData } from "@/content";
import { useMediaByKey, usePillar } from "@/content-context";
import type { MediaItem } from "@/data/media";
import { getPillar, type Pillar } from "@/data/programs";
import { buildMetadata } from "@/lib/seo";
import { imageReveal } from "@/lib/motion";
import { contentVersionFrom, usePageData } from "@/page-data";

/**
 * A programme page: Women & Girls, Children & Education and the rest.
 *
 * Each one is a page with sections now, edited under Pages → Our Work, in the
 * same way as the homepage: an introduction, the activities, photographs, the
 * projects and stories filed under the programme, and a closing call to action,
 * each of which can be edited, hidden or moved. The programme's name and outcome
 * line still come from its Programme record, because the homepage card shows the
 * same words, and the banner follows them unless the page sets its own.
 *
 * The slug stays code-owned. An unknown one is a real 404, decided before
 * anything renders.
 */
export async function loader({ params, request, context }: LoaderFunctionArgs) {
  const slug = String(params.pillar);
  if (!getPillar(slug)) {
    throw new Response("No programme pillar with that slug.", { status: 404 });
  }
  return fetchPageData(slug, { signal: request.signal, version: contentVersionFrom(context) });
}

export const meta = ({ params }: { params: { pillar?: string } }) => {
  const pillar = getPillar(String(params.pillar));
  return pillar
    ? buildMetadata({
        title: pillar.title,
        description: pillar.outcome,
        path: `/our-work/${pillar.slug}`,
      })
    : null;
};

/** The programme this page belongs to, with the CMS overlay applied. */
function useThisPillar(): Pillar {
  const data = usePageData();
  const param = useParams().pillar;
  const slug = data?.slug ?? String(param);
  return usePillar(slug) ?? getPillar(slug)!;
}

function IntroductionSection({ section }: SectionProps) {
  const pillar = useThisPillar();
  const byKey = useMediaByKey();
  const image = byKey(section.mediaKey) ?? pillar.image;
  const approach = visibleItems(section);
  const figures = section.facts ?? [];

  return (
    <Section>
      <div className="grid items-start gap-12 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-16">
        <Reveal>
          {section.lead ? (
            <p className="prose-body text-ink" style={{ fontSize: "var(--step-lead)" }}>
              {section.lead}
            </p>
          ) : null}

          {approach.length ? (
            <>
              {section.title ? (
                <h2 className="mt-10" style={{ fontSize: "var(--step-h3)" }}>
                  {section.title}
                </h2>
              ) : null}
              <ul className="mt-5 space-y-4">
                {approach.map((line) => (
                  <li key={line.title} className="flex gap-3.5 text-muted">
                    <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue" />
                    {line.title}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </Reveal>

        <Reveal variants={imageReveal}>
          <MediaFigure
            item={image}
            ratio="4 / 3"
            sizes="(min-width: 1024px) 45vw, 100vw"
            objectPosition={chosenFocalPoint(section.settings?.focalPoint)}
          />

          {figures.length ? (
            <dl className="mt-8 rounded-[16px] border border-line bg-mist p-6">
              {section.eyebrow ? (
                <h2 className="text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-blue">{section.eyebrow}</h2>
              ) : null}
              <div className="mt-4 space-y-3">
                {figures.map((fact) => (
                  <div
                    key={fact.label}
                    className="flex flex-wrap justify-between gap-x-6 gap-y-1 border-b border-line pb-3 last:border-0 last:pb-0"
                  >
                    <dt className="text-[0.9375rem] text-muted">{fact.label}</dt>
                    <dd className="font-serif text-[1.0625rem] text-midnight">{fact.value}</dd>
                  </div>
                ))}
              </div>
            </dl>
          ) : null}
        </Reveal>
      </div>
    </Section>
  );
}

function ActivitiesSection({ section }: SectionProps) {
  const activities = visibleItems(section);
  if (!activities.length) return null;
  return (
    <Section tone="ivory" ariaLabelledBy="activities-heading">
      <SectionHeading id="activities-heading" eyebrow={section.eyebrow} title={section.title || "Activities"} lead={section.lead} />
      <RevealGroup as="ul" className="mt-10 grid gap-6 md:grid-cols-2">
        {activities.map((activity) => (
          <Reveal as="li" key={activity.title}>
            <div className="h-full rounded-[16px] border border-line bg-white p-6">
              <h3 style={{ fontSize: "var(--step-h4)" }}>{activity.title}</h3>
              {activity.text ? <p className="mt-3 text-muted">{activity.text}</p> : null}
            </div>
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

function GallerySection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const photos = (section.mediaKeys ?? []).map((key) => byKey(key)).filter((item): item is MediaItem => Boolean(item));
  if (!photos.length) return null;
  return (
    <Section ariaLabelledBy="pillar-gallery-heading">
      <SectionHeading id="pillar-gallery-heading" eyebrow={section.eyebrow} title={section.title || "Photographs"} />
      <RevealGroup as="ul" className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {photos.map((media) => (
          <Reveal as="li" key={media.id}>
            <MediaFigure item={media} ratio="4 / 3" sizes="(min-width: 1024px) 30vw, 100vw" />
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

function ProjectsSection({ section }: SectionProps) {
  const pillar = useThisPillar();
  return <ProjectListSection section={{ ...section, settings: { ...section.settings, tone: "mist" } }} programmeSlug={pillar.slug} />;
}

function StoriesSection({ section }: SectionProps) {
  const pillar = useThisPillar();
  return <StoryListSection section={{ ...section, settings: { ...section.settings, tone: "white" } }} programmeSlug={pillar.slug} />;
}

function SupportSection({ section }: SectionProps) {
  const pillar = useThisPillar();
  return (
    <CtaBand section={{ ...section, settings: { ...section.settings, tone: "navy" } }}>
      <p className="mt-10 text-[0.875rem] text-white/60">
        Source for this programme:{" "}
        <a href={pillar.source} target="_blank" rel="noreferrer noopener" className="text-sky underline underline-offset-4">
          {pillar.source}
        </a>
      </p>
    </CtaBand>
  );
}

export const programmeSections: SectionRenderers = {
  "*.introduction": IntroductionSection,
  "*.activities": ActivitiesSection,
  "*.gallery": GallerySection,
  "*.projects": ProjectsSection,
  "*.stories": StoriesSection,
  "*.support": SupportSection,
};

export function PillarPage() {
  const pillar = useThisPillar();
  return (
    <>
      <PageHeader
        eyebrow="Programme pillar"
        title={pillar.title}
        lead={pillar.outcome}
        trail={[
          { name: "Home", href: "/" },
          { name: "Our Work", href: "/our-work" },
          { name: pillar.title, href: `/our-work/${pillar.slug}` },
        ]}
      />
      <PageSections renderers={programmeSections} />
    </>
  );
}
